import _ from 'lodash';

import { SESSION_LENGTH_MINUTES } from './constants';
import { SessionAttendanceSummary } from '../../components/attendance/types';
import { ClassStudent } from '../types';

const ONE_MINUTE = 60;
const SESSION_LENGTH = ONE_MINUTE * SESSION_LENGTH_MINUTES;

interface CountByRoleProps {
    students: ClassStudent[];
    sessionAttendance: SessionAttendanceSummary;
}

const countStudentsInAttendance = ({ students, sessionAttendance }: CountByRoleProps): number => {
    const count = students.reduce((total, participant) => {
        const hasAttendance = sessionAttendance.students[participant.student_id].marks?.marked_present || false;

        if (participant.role === 'student' && hasAttendance) {
            return total + 1;
        }
        return total;
    }, 0);

    return count;
};

const calcTeacherTalkTime = (): number => {
    const TEACHER_TALK_PERCENT_MIN = 0.4;
    const TEACHER_TALK_PERCENT_MAX = 0.85;
    const teacherTalkTime = _.random(
        SESSION_LENGTH * TEACHER_TALK_PERCENT_MIN,
        SESSION_LENGTH * TEACHER_TALK_PERCENT_MAX
    );

    return teacherTalkTime;
};

interface CalcStudentTalkTimeProps {
    teacherTalkTime: number;
    studentId: string;
    students: ClassStudent[];
    sessionAttendance: SessionAttendanceSummary;
}

const calcStudentTalkTime = ({
    teacherTalkTime,
    students,
    studentId,
    sessionAttendance
}: CalcStudentTalkTimeProps): number => {
    const hasAttendance = sessionAttendance.students[studentId].marks?.marked_present || false;

    if (!hasAttendance) {
        return 0;
    }

    const STUDENT_PERCENT_WITH_ZERO_TALK_TIME = 0.2;
    const QUIET_TIME = SESSION_LENGTH * 0.1;
    const studentTalkTimeAvailable = SESSION_LENGTH - QUIET_TIME - teacherTalkTime;

    const studentCount = countStudentsInAttendance({ students, sessionAttendance });

    const silentStudentCount = studentCount * STUDENT_PERCENT_WITH_ZERO_TALK_TIME;

    const variability = _.random(0, 2, true);
    const studentTalkTime = (studentTalkTimeAvailable / (studentCount - silentStudentCount)) * variability;

    if (Math.random() < STUDENT_PERCENT_WITH_ZERO_TALK_TIME) {
        return 0;
    }

    return studentTalkTime;
};

interface MakeSessionMetricDataProps {
    students: ClassStudent[];
    sessionAttendance: SessionAttendanceSummary;
}
// creates realistic data for each participant in each session
export const makeSessionMetricData = ({ students, sessionAttendance }: MakeSessionMetricDataProps) => {
    const sessionsMetricDemoData = {};
    const teacherTalkTime = calcTeacherTalkTime();

    students.forEach((student) => {
        const studentTalkTime = calcStudentTalkTime({
            teacherTalkTime,
            studentId: student.student_id,
            students,
            sessionAttendance
        });

        switch (student.role) {
            case 'teacher':
                sessionsMetricDemoData[student.student_id] = {
                    talk_time: teacherTalkTime,
                    talk_count: _.random(10, 20),
                    focus_time: _.random(SESSION_LENGTH * 0.33, ONE_MINUTE * 0.66),
                    focus_count: _.random(5, 10),
                    hand_raised_time: _.random(0, SESSION_LENGTH * 0.02),
                    hand_raised_count: _.random(0, 1),
                    feedback_time: _.random(0, SESSION_LENGTH * 0.02),
                    feedback_count: _.random(0, 10),
                    stars_max: 0
                };
                break;
            case 'assistant':
                sessionsMetricDemoData[student.student_id] = {
                    talk_time: _.random(SESSION_LENGTH * 0.02, SESSION_LENGTH * 0.05),
                    talk_count: _.random(10, 10),
                    focus_time: _.random(SESSION_LENGTH * 0.33, ONE_MINUTE * 0.66),
                    focus_count: _.random(5, 10),
                    hand_raised_time: _.random(0, SESSION_LENGTH * 0.02),
                    hand_raised_count: _.random(0, 10),
                    feedback_time: _.random(0, SESSION_LENGTH * 0.02),
                    feedback_count: _.random(0, 10),
                    stars_max: _.random(0, 1)
                };
                break;
            case 'student':
                sessionsMetricDemoData[student.student_id] = {
                    talk_time: studentTalkTime,
                    talk_count: _.random(1, 3),
                    focus_time: _.random(ONE_MINUTE * 30, ONE_MINUTE * 90),
                    focus_count: _.random(0, 200),
                    hand_raised_time: _.random(0, ONE_MINUTE),
                    hand_raised_count: _.random(0, 2),
                    feedback_time: _.random(0, ONE_MINUTE),
                    feedback_count: _.random(0, 3),
                    stars_max: _.random(0, 5)
                };
                break;
            default:
                break;
        }
    });

    return sessionsMetricDemoData;
};
